/**
 * Reads a Vimeo URL/ID out of a clipboard paste event — shared by both class editors
 * (course-content.component.ts and admin-class-zero.component.ts) so pasting a share link or a
 * bare ID behaves identically in either. Falls back to the raw trimmed paste when it doesn't look
 * like a Vimeo URL/ID, same as before this was extracted: the FormControl's own
 * `Validators.pattern` is what surfaces an error, not this function.
 */
export function extractVimeoIdFromClipboardEvent(event: ClipboardEvent): string {
  const clipboardData = event.clipboardData || (window as any).clipboardData;
  const pastedText = (clipboardData?.getData('text') ?? '').trim();
  return extractVimeoVideoId(pastedText) ?? pastedText;
}

export function extractVimeoVideoId(value: string | null | undefined): string | null {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d{9,12}$/.test(trimmed)) return trimmed;

  const bareIdWithHashMatch = trimmed.match(/^(\d{9,12})\/([a-z0-9]+)$/i);
  if (bareIdWithHashMatch) return bareIdWithHashMatch[1];

  try {
    const parsedUrl = new URL(trimmed);
    const hostname = parsedUrl.hostname.toLowerCase();

    if (hostname === 'player.vimeo.com') {
      const match = parsedUrl.pathname.match(/^\/video\/(\d+)(?:\/.*)?$/);
      return match?.[1] ?? null;
    }

    if (hostname === 'vimeo.com' || hostname === 'www.vimeo.com') {
      const match = parsedUrl.pathname.match(/^\/(\d+)(?:\/[^/?#]+)?(?:\/)?$/);
      return match?.[1] ?? null;
    }
  } catch {
    // ignore
  }

  return null;
}

export function getVimeoEmbedUrl(value: string | null | undefined): string | null {
  const videoId = extractVimeoVideoId(value);
  if (!videoId) return null;

  const trimmed = value?.trim();
  if (!trimmed) return null;

  const params = new URLSearchParams({
    badge: '0',
    autopause: '0',
    player_id: '0',
    app_id: '58479'
  });

  try {
    const parsedUrl = new URL(trimmed);
    const hostname = parsedUrl.hostname.toLowerCase();
    const hashFromQuery = parsedUrl.searchParams.get('h');
    if (hashFromQuery) {
      params.set('h', hashFromQuery);
    } else if (hostname === 'vimeo.com' || hostname === 'www.vimeo.com') {
      const segments = parsedUrl.pathname.split('/').filter(Boolean);
      const hash = segments[1];
      if (hash && /^[a-z0-9]+$/i.test(hash)) {
        params.set('h', hash);
      }
    }
  } catch {
    const bareIdWithHashMatch = trimmed.match(/^(\d{9,12})\/([a-z0-9]+)$/i);
    if (bareIdWithHashMatch) {
      params.set('h', bareIdWithHashMatch[2]);
    }
  }

  return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
}
